import React, { useEffect, useRef, useState } from 'react';
import { Banner } from './components/Banner';
import { Button, ButtonStyles, Icon, Icons, Popup, Tabs } from '@silverfort/sf-components';
import ComponentsOverview from './ComponentsOverview';
import EnvironmentStatus from './EnvironmentStatus';
import UpdateWizard from './updateWizard/UpdateWizard';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import useFeature from '../../SystemSettings/hooks/featureFlags/useFeature';
import {
    calcTabs,
    checkConnectivityHandler,
    checkForUpdatesHandler,
    DEFAULT_STATUS,
    NODES_TYPES
} from './EnvironmentOverviewUtils';
import { DcComponent, NodeComponent, PushServiceComponent, SoftwareStatus } from './types';
import { httpPost, isUserPermitted } from '../../utils';
import { useGetUserData } from '../../../api';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { CircularProgress } from '@material-ui/core';

const EnvironmentOverview: React.FC = () => {
    const statusInterval = useRef<NodeJS.Timeout | null>(null);
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const [showWizard, setShowWizard] = useState(false);
    const [nodes, setNodes] = useState<NodeComponent[]>([]);
    const [dcs, setDcs] = useState<DcComponent[]>([]);
    const [pushServices, setPushServices] = useState<PushServiceComponent[]>([]);
    const [softwareStatus, setSoftwareStatus] = useState<SoftwareStatus>(DEFAULT_STATUS);
    const [upgradeBlockers, setUpgradeBlockers] = useState<string[]>([]);
    const [showBlockersPopup, setShowBlockersPopup] = useState(false);
    const [userExitWizard, setUserExitWizard] = useState(false);
    const [selectedTab, setSelectedTab] = useState<string>(NODES_TYPES.PUSH_SERVICES);
    const { userSession } = useGetUserData();
    const [loading, setLoading] = useState(true);
    const [nodesDataLoaded, setNodesDataLoaded] = useState(false);
    const isEditor = userSession && isUserPermitted(userSession.user, 'softwareUpdates', ROLES.EDITOR.value);

    const setNodesData = (data) => {
        setNodes(data.nodes);
        setDcs(data.dcs);
        setPushServices(data.pushServices);

        setSelectedTab((currentSelectedTab) => {
            if (currentSelectedTab === NODES_TYPES.PUSH_SERVICES && !data.pushServices?.length) {
                return NODES_TYPES.NODES;
            }
            return currentSelectedTab;
        });

        setNodesDataLoaded(true);
    };

    const handleUpgradeClick = async () => {
        try {
            if (upgradeBlockers.length > 0) {
                setShowBlockersPopup(true);
                return;
            }

            if (softwareStatus.versionUpgradeInProgress) {
                await httpPost('/softwareUpdates/finishUpgradeProgress', { abortCurrentProcess: true });
                setSoftwareStatus((prevStatus) => ({
                    ...prevStatus,
                    versionUpgradeInProgress: null
                }));
            }

            setShowWizard(true);
            setUserExitWizard(false);
        } catch (error) {
            console.error('Error finishing current upgrade progress:', error);
        }
    };

    const handleWizardFinish = async () => {
        await httpPost('/softwareUpdates/finishUpgradeProgress');
        window.location.href = `/?upgradeCompleted=true&version=${softwareStatus.currentTowerVersion}`;
    };

    useEffect(() => {
        checkForUpdatesHandler({
            setSoftwareStatus,
            statusInterval,
            setNodesData
        });
        checkConnectivityHandler({
            systemMode,
            setNodesData,
            setSoftwareStatus
        });

        return () => {
            if (statusInterval.current) {
                clearInterval(statusInterval.current);
            }
        };
    }, [systemMode]);

    useEffect(() => {
        const upgradeBlockers: string[] = [];

        if (!softwareStatus.quayConnected) {
            upgradeBlockers.push(
                'Cannot connect to Quay. Verify firewall settings or disable SSL inspection if enabled.'
            );
        }

        if (!softwareStatus.diskSpaceOk) {
            upgradeBlockers.push(`Not enough disk space to download the package. At least 50GB of free space is required.
                Free up space to continue`);
        }

        if (!softwareStatus.certificateOK) {
            upgradeBlockers.push('Admin Console certificate has expired or is invalid.');
        }

        if (
            softwareStatus.downloading ||
            softwareStatus.upgradingTower ||
            softwareStatus.upgradingNodes ||
            softwareStatus.upgradingPushServices ||
            (softwareStatus.upgradingDcs && !softwareStatus.autoADAdapterUpgrade)
        ) {
            setShowWizard(true);
        }

        setUpgradeBlockers(upgradeBlockers);

        softwareStatus.companyName && nodesDataLoaded && setLoading(false);
    }, [softwareStatus, nodesDataLoaded]);

    return (
        <div className="bg-sf-white w-[calc(100%-64px)] h-[calc(100%-64px)] ml-16 mt-16 font-sf">
            {loading ? (
                <div className="bg-sf-gray-50 w-full h-full flex justify-center items-center">
                    <div className="flex flex-col gap-4 items-center h-16 w-full">
                        <CircularProgress size={24} />
                        <div className="text-16 font-sf text-sf-gray-800">Collecting software update data...</div>
                    </div>
                </div>
            ) : (
                <div className="w-full h-full flex flex-col">
                    {showBlockersPopup && (
                        <Popup
                            open={showBlockersPopup}
                            title="Issues to resolve"
                            ContentComponent={() => (
                                <div className="flex flex-col gap-6 pb-2">
                                    <div>
                                        {`You have ${upgradeBlockers.length} issues to resolve before you can proceed
                                        with the upgrade:`}
                                    </div>
                                    {upgradeBlockers.map((blocker, index) => (
                                        <div key={index} className="flex gap-4 text-13 items-center">
                                            <Icon size={16} icon={Icons.Warning} className="text-sf-neutral-500" />
                                            {blocker}
                                        </div>
                                    ))}
                                </div>
                            )}
                            FooterComponent={() => (
                                <div className="flex justify-end items-center gap-2">
                                    <Button
                                        buttonStyle={ButtonStyles.Tertiary}
                                        text="Close"
                                        onClick={() => setShowBlockersPopup(false)}></Button>
                                    <Button
                                        buttonStyle={ButtonStyles.PrimarySolid}
                                        text="Check Again"
                                        onClick={() => {
                                            checkForUpdatesHandler({
                                                setSoftwareStatus,
                                                statusInterval,
                                                setNodesData
                                            });

                                            setShowBlockersPopup(false);
                                        }}></Button>
                                </div>
                            )}
                            onClose={() => setShowBlockersPopup(false)}
                        />
                    )}
                    {!!upgradeBlockers.length && (!showWizard || userExitWizard) && (
                        <div className="p-5">
                            <Banner
                                theme="Attention"
                                actionButtonProps={{
                                    text: 'Review Issues',
                                    onClick: () => setShowBlockersPopup(true)
                                }}>
                                <Banner.Body
                                    content={
                                        <span>
                                            You have <span className="font-bold">{upgradeBlockers.length}</span> issues
                                            to resolve before you can proceed with the upgrade.
                                        </span>
                                    }
                                />
                            </Banner>
                        </div>
                    )}
                    {showWizard && !userExitWizard ? (
                        <UpdateWizard
                            softwareStatus={softwareStatus}
                            serversData={{ nodes, dcs, pushServices }}
                            onCancel={() => {
                                setShowWizard(false);
                                setUserExitWizard(true);
                            }}
                            onFinish={handleWizardFinish}
                        />
                    ) : (
                        <>
                            <EnvironmentStatus
                                serversData={{ nodes, dcs, pushServices }}
                                softwareStatus={softwareStatus}
                                onUpgradeClick={() => handleUpgradeClick()}
                                onContinueClick={() => {
                                    if (upgradeBlockers.length > 0) {
                                        setShowBlockersPopup(true);
                                        return;
                                    }

                                    setShowWizard(true);
                                    setUserExitWizard(false);
                                }}
                                isEditor={isEditor}
                            />
                            <div className="flex-1 w-full px-6 flex flex-col gap-5 bg-sf-white">
                                <Tabs
                                    tabs={calcTabs(nodes, dcs, pushServices)}
                                    selectedTab={selectedTab}
                                    onClick={(tab) => {
                                        setSelectedTab(tab);
                                    }}
                                />
                                <ComponentsOverview
                                    setSoftwareStatus={setSoftwareStatus}
                                    selectedTab={selectedTab}
                                    softwareStatus={softwareStatus}
                                    serversData={{ nodes, dcs, pushServices }}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default EnvironmentOverview;
